import { Box, Grid } from "@material-ui/core";
import DESC_ICON from "assets/img/arrow-down.svg";
import ASC_ICON from "assets/img/arrow-up.svg";
import SEARCH_ICON from "assets/img/filter-data.svg";
import INFORMATION_ICON from "assets/img/information.svg";
import { SORTING_ORDER } from "enum/sorting.enum";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "./LightThemeModal";
import Loader from "./Loader";
import NoDataAvailable from "./NoDataLightTheme";

export interface ApiTableColumn {
  field: string;
  type?: string;
  header: React.ReactNode;
  minWidth?: number;
  maxWidth?: number;
  sortable?: boolean;
  searchable?: boolean;
  render?: (row: any) => React.ReactNode;
  formatter?: (value: any, row: any) => React.ReactNode;
}

export interface ApiTableProps<T> {
  columns: ApiTableColumn[];
  data: T[];
  loading?: boolean;
  page: number;
  pageSize: number;
  total: number;
  onSearch: (field: string, value: string) => void;
  onSort: (field: string, order: SORTING_ORDER) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onRowClick?: (row: T) => void;
  pageSizeOptions?: number[];
  clearSearch?: boolean;
  roles?: any[];

  // selection props (parent-managed)
  selectedRows?: (string | number)[]; // exceptions when selectAllActive is ON, or selected ids when OFF
  selectAllActive?: boolean; // true => "select all across all pages" active
  onRowSelect?: (row: T) => void; // called when a row checkbox toggles
  onSelectAll?: () => void; // called when header checkbox toggles
}

/* ---------------- Pagination ---------------- */
const Pagination = ({
  page,
  pageSize,
  total,
  pageSizeOptions = [100],
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number, pageSize: number) => void;
}) => {
  const [gotoPage, setGotoPage] = useState<string>("");
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) pages.push(1, 2, 3, 4, "...", totalPages);
      else if (page >= totalPages - 2)
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      else pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
    }
    return pages;
  };

  const handlePageChange = (newPage: number) => {
    const safePage = Math.max(1, Math.min(newPage, totalPages));
    onPageChange(safePage, pageSize);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    onPageChange(1, newSize);
  };

  const handleGotoPage = () => {
    const pageNum = Number(gotoPage);
    if (!isNaN(pageNum)) handlePageChange(pageNum);
    setGotoPage("");
  };

  return (
    <Grid container spacing={2} style={{ padding: "16px 8px" }}>
      {/* Page Size */}
      <Grid xs={12} md={4} spacing={1}>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
          Rows per page:
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 60, padding: "4px 6px", fontSize: 12, cursor: "pointer" }}
          >
            {pageSizeOptions!.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </Grid>

      {/* Page Numbers */}
      <Grid xs={12} md={4}>
        <Box
          flex="1 1 auto"
          display="flex"
          justifyContent={{ xs: "flex-start", md: "center" }}
          alignItems="center"
        >
          <span
            onClick={() => handlePageChange(page - 1)}
            style={{
              cursor: page === 1 ? "not-allowed" : "pointer",
              color: page === 1 ? "#ccc" : "#000",
              fontWeight: "bold",
              fontSize: 12,
            }}
          >
            &lt;
          </span>

          {getPageNumbers().map((p, idx) =>
            typeof p === "number" ? (
              <span
                key={idx}
                onClick={() => handlePageChange(p)}
                style={{
                  cursor: "pointer",
                  padding: "4px 8px",
                  fontSize: 12,
                  borderBottom: page === p ? "2px solid #E5B42F" : "2px solid transparent",
                }}
              >
                {p}
              </span>
            ) : (
              <span key={idx} style={{ padding: "4px 8px", fontSize: 12, cursor: "default" }}>
                {p}
              </span>
            )
          )}

          <span
            onClick={() => handlePageChange(page + 1)}
            style={{
              cursor: page === totalPages ? "not-allowed" : "pointer",
              color: page === totalPages ? "#ccc" : "#000",
              fontWeight: "bold",
              fontSize: 12,
            }}
          >
            &gt;
          </span>
        </Box>
      </Grid>

      {/* Go To Page */}
      <Grid xs={12} md={4}>
        <Box
          style={{ gap: "6px" }}
          display="flex"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          alignItems="center"
          fontSize={12}
        >
          <span>Go To</span>
          <input
            type="text"
            value={gotoPage}
            onChange={(e) => setGotoPage(e.target.value)}
            placeholder="Page"
            onKeyDown={(e) => e.key === "Enter" && handleGotoPage()}
            style={{ width: 60, padding: "4px 6px", fontSize: 12 }}
          />
          <button
            onClick={handleGotoPage}
            style={{
              backgroundColor: "#E5B42F",
              border: "none",
              padding: "5px 12px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Go
          </button>
        </Box>
      </Grid>
    </Grid>
  );
};

/* ---------------- ApiTable ---------------- */
export default function ApiTable<T>({
  columns,
  data,
  total,
  loading = false,
  page,
  pageSize,
  onSearch,
  onSort,
  onPageChange,
  onRowClick,
  pageSizeOptions = [100],
  clearSearch,
  selectedRows = [],
  selectAllActive = false,
  onRowSelect,
  onSelectAll,
  roles = [],
}: ApiTableProps<T>) {
  const [searchFields, setSearchFields] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  const lastClearRef = useRef<boolean>(false);
  const [roleModal, setRoleModal] = useState(false);

  useEffect(() => {
    if (clearSearch && clearSearch !== lastClearRef.current) {
      lastClearRef.current = clearSearch;
      setSearchFields({});
    }
  }, [clearSearch]);

  const handleSort = useCallback(
    (field: string) => {
      const newOrder =
        sortField === field
          ? sortOrder === SORTING_ORDER.ASC
            ? SORTING_ORDER.DESC
            : SORTING_ORDER.ASC
          : SORTING_ORDER.ASC;
      setSortField(field);
      setSortOrder(newOrder);
      onSort(field, newOrder);
    },
    [sortField, sortOrder, onSort]
  );

  const handleClearSearch = (field: string) => {
    setSearchFields((prev) => ({ ...prev, [field]: "" }));
    onSearch(field, "");
  };

  const handleSearchChange = (colField: string, value: string) => {
    setSearchFields((prev) => ({ ...prev, [colField]: value }));
  };

  const makeSearch = (field: string, value: string) => {
    onSearch(field, value || "");
  };

  // Determine if table has a checkbox column (we will render it first)
  const hasCheckboxColumn = useMemo(() => columns.some((c) => c.field === "checkbox"), [columns]);
  /* ---------------- Table Rows ---------------- */
  const tableRows = useMemo(() => {
    if (loading)
      return (
        <tr>
          <td
            colSpan={columns.length + (hasCheckboxColumn ? 0 : 0)}
            style={{ textAlign: "center", padding: 20 }}
          >
            <Loader />
          </td>
        </tr>
      );

    if (!data || data.length === 0)
      return (
        <tr>
          <td
            colSpan={columns.length + (hasCheckboxColumn ? 0 : 0)}
            style={{ textAlign: "center", padding: 20 }}
          >
            <NoDataAvailable />
          </td>
        </tr>
      );

    return data.map((row: any, idx: number) => {
      // unique key - fallback to idx if no id
      const rowKey = String(row.chain_user_role_id ?? idx);

      return (
        <tr
          key={rowKey}
          onClick={(e) => {
            // Only trigger row click if click was not on a checkbox
            if (!(e.target as HTMLElement).closest("input[type='checkbox']")) {
              onRowClick?.(row);
            }
          }}
          style={{
            cursor: onRowClick ? "pointer" : "default",
            backgroundColor: idx % 2 === 0 ? "#FFFFFF" : "#FEFCE8",
          }}
        >
          {/* ---------------- Checkbox column - FIRST ---------------- */}
          {hasCheckboxColumn &&
            (() => {
              const id = row.chain_user_role_id;
              // When selectAllActive is true -> selectedRows[] stores exceptions (unselected ids)
              const checked = selectAllActive
                ? !selectedRows.includes(id)
                : selectedRows.includes(id);

              return (
                <td
                  key="checkbox"
                  style={{
                    padding: "5px 12px",
                    width: 40,
                    minWidth: 40,
                    maxWidth: 40,
                    whiteSpace: "nowrap",
                    borderBottomWidth: "none",
                    borderBottomStyle: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    style={{ accentColor: "#E5B42F" } as any}
                    onChange={(e) => {
                      e.stopPropagation();
                      onRowSelect && onRowSelect(row);
                    }}
                    className="custom-checkbox"
                  />
                </td>
              );
            })()}

          {/* ---------------- Other columns ---------------- */}
          {columns
            .filter((c) => c.field !== "checkbox")
            .map((col) => {
              const value = col.formatter
                ? col.formatter((row as any)[col.field], row)
                : col.render
                ? col.render(row)
                : (row as any)[col.field] ?? "-";
              const displayValue = typeof value === "string" && value.length > 25 ? value : value;

              return (
                <td
                  key={col.field}
                  title={typeof value === "string" && value.length > 25 ? undefined : undefined}
                  style={{
                    padding: "5px 12px",
                    borderBottom: "2px solid #FDF19EC2",
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayValue}
                </td>
              );
            })}
        </tr>
      );
    });
  }, [
    data,
    loading,
    columns,
    selectedRows,
    selectAllActive,
    onRowSelect,
    onRowClick,
    hasCheckboxColumn,
  ]);

  /* ---------------- Table Header ---------------- */
  // header checkbox indeterminate handling
  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    // compute ids on current page
    const pageIds: (string | number)[] = data.map((r: any) => r.chain_user_role_id).filter(Boolean);
    if (pageIds.length === 0) {
      headerCheckboxRef.current.checked = false;
      headerCheckboxRef.current.indeterminate = false;
      return;
    }

    if (selectAllActive) {
      // global select all => header should be checked (not indeterminate)
      headerCheckboxRef.current.checked = true;
      headerCheckboxRef.current.indeterminate = false;
      return;
    }

    const allChecked = pageIds.every((id) => selectedRows.includes(id));
    const someChecked = pageIds.some((id) => selectedRows.includes(id));
    headerCheckboxRef.current.checked = allChecked;
    headerCheckboxRef.current.indeterminate = !allChecked && someChecked;
  }, [data, selectedRows, selectAllActive]);

  const tableHeader = useMemo(() => {
    // We will render checkbox header first (if present),
    // then remaining columns (excluding checkbox)
    const nonCheckboxColumns = columns.filter((c) => c.field !== "checkbox");

    return (
      <tr>
        {hasCheckboxColumn && (
          <th
            key="checkbox"
            style={{ padding: "12px 10px", width: 40, minWidth: 40, textAlign: "center" }}
          >
            <input
              ref={headerCheckboxRef}
              type="checkbox"
              // `checked` and `indeterminate` are handled in effect above
              style={{ accentColor: "#E5B42F" } as any}
              onChange={(e) => {
                e.stopPropagation();
                onSelectAll && onSelectAll();
              }}
              className="custom-checkbox"
            />
          </th>
        )}

        {nonCheckboxColumns.map((col) => {
          const isSorted = sortField === col.field;
          const searchValue = searchFields[col.field] ?? "";
          const isActionColumn = col.sortable === false && col.searchable === false;
          const showColumnControls = col.header && !isActionColumn;

          return (
            <th
              key={col.field}
              style={{
                minWidth: col.minWidth,
                maxWidth: col.maxWidth,
                textAlign: "left",
                padding: "12px 10px",
                borderBottom: "2px solid #E3E6EA",
                fontWeight: "bold",
                fontSize: 12,
                whiteSpace: "nowrap",
                position: "relative",
              }}
            >
              {col.header && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: col.sortable !== false ? "pointer" : "default",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        col.sortable !== false && handleSort(col.field);
                      }}
                    >
                      {col.header}
                      {showColumnControls && col.sortable !== false && (
                        <img
                          src={isSorted ? (sortOrder === "asc" ? ASC_ICON : DESC_ICON) : ASC_ICON}
                          alt="sort"
                          style={{ width: 10, opacity: isSorted ? 1 : 0.4 }}
                        />
                      )}
                    </span>

                    {/* INFO ICON */}
                    {col.header === "Role Mapping" && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setRoleModal(true);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <img src={INFORMATION_ICON} alt="info" style={{ width: 22 }} />
                      </span>
                    )}
                  </div>

                  {showColumnControls && col.searchable !== false && (
                    <div style={{ position: "relative", marginTop: 6 }}>
                      <input
                        type={col.type === "date" ? "date" : "text"}
                        value={searchFields[col.field] || ""}
                        onChange={(e) => {
                          handleSearchChange(col.field, e.target.value);
                          if (col.type === "date") {
                            makeSearch(col.field, e.target.value);
                          }
                        }}
                        placeholder={`Search ${col.header}`}
                        style={{
                          width: "100%",
                          padding: col.type === "date" ? "6px 6px 6px 6px" : "6px 28px 6px 6px",
                          fontSize: 12,
                          border: "1px solid #D1D5DB",
                          borderRadius: 4,
                        }}
                      />
                      {col.type !== "date" && (
                        <img
                          onClick={() => makeSearch(col.field, searchFields[col.field] || "")}
                          src={SEARCH_ICON}
                          style={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 14,
                            opacity: (searchFields[col.field] || "").trim() === "" ? 0.3 : 0.6,
                            cursor: (searchFields[col.field] || "").trim() === "" ? "not-allowed" : "pointer",
                            pointerEvents: (searchFields[col.field] || "").trim() === "" ? "none" : "auto",
                          }}
                        />
                      )}
                      {searchValue !== "" && (
                        <span
                          onClick={() => handleClearSearch(col.field)}
                          style={{
                            position: "absolute",
                            right: 28,
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: 16,
                            fontWeight: "bold",
                            color: "#555",
                            userSelect: "none",
                          }}
                        >
                          ×
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </th>
          );
        })}
      </tr>
    );
  }, [
    columns,
    data,
    searchFields,
    sortField,
    sortOrder,
    selectedRows,
    selectAllActive,
    onRowSelect,
    onSelectAll,
    hasCheckboxColumn,
  ]);

  return (
    <div className="api-table-body-wrapper" style={{ width: "100%" }}>
      <div className="api-table-body-wrapper" style={{overflow: "auto"}}>
      <table className="api-table-body-wrapper" style={{width: "100%" }}>
        <thead style={{ backgroundColor: "#F9FAFB" }}>{tableHeader}</thead>
        <tbody>{tableRows}</tbody>
      </table>
      </div>
      {data.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          pageSizeOptions={pageSizeOptions}
        />
      )}
      <Modal
        show={roleModal}
        onClose={() => setRoleModal(false)}
        title="Roles"
        width="400px"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 10,
            }}
          ></div>
        }
      >
        {/* Modal Body */}
        <div style={{ fontSize: 12, color: "#333" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead></thead>
            <tbody>
              {roles.map((role, index) => (
                <tr key={role.id}>
                  <td
                    style={{
                      padding: "5px 0px",
                      borderBottom: "2px solid #FDF19EC2",
                      fontSize: 12,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {index + 1}.
                  </td>
                  <td
                    style={{
                      padding: "5px 0px",
                      borderBottom: "2px solid #FDF19EC2",
                      fontSize: 12,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {role.role}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}
